import { Controller } from "@hotwired/stimulus"
import { isAbortError } from "../../utils/fetch-error.util"

// Tiers that map to exactly one country. :eu and :other are intentionally
// absent — either can represent many countries, so their country_code comes
// from the detected IBAN's own parsed.country_code (paste-detect only)
// rather than a fixed lookup.
const TIER_COUNTRY_CODES = { cz: "CZ", gb: "GB", us: "US", ca: "CA", au: "AU", in: "IN", jp: "JP" }

// Paste-and-detect: debounced fetch to the server-side detection endpoint,
// following the same pattern as legal_entity_controller.js (AbortController
// cancellation of stale requests). Field-swap on detection follows
// entity_type_toggle_controller.js's show/hide pattern — no second network
// round-trip for the field swap itself, since every tier's partial is
// already server-rendered into the page (see _bank_account_fields.html.erb).
//
// Every tier section is a `<fieldset>`, not a plain `<div>` — several field
// names (currency, account_number, swift_code, bank_code, branch_code) are
// shared across 2+ tiers, so a hidden tier's blank
// inputs would otherwise still be submitted and silently clobber the visible
// tier's values. `#showTier()` MUST toggle `.disabled` on every fieldset in
// lockstep with `style.display` — that's what actually excludes a hidden
// tier's inputs from the form submission; `display:none` alone does not.
export default class extends Controller {
  static targets = ["pasteInput", "pasteResult", "countrySelect", "tierSection", "countryCodeInput"]
  static values = { url: String, debounceDelay: { type: Number, default: 300 } }

  connect() {
    this.abortController = null
    this.debounceTimer = null
  }

  disconnect() {
    this.abortController?.abort()
    clearTimeout(this.debounceTimer)
  }

  pasteInputTargetConnected(element) {
    element.addEventListener("input", () => this.scheduleDetect())
  }

  scheduleDetect() {
    clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => this.detect(), this.debounceDelayValue)
  }

  async detect() {
    const raw = this.pasteInputTarget.value.trim()
    if (!raw) return

    this.abortController?.abort()
    this.abortController = new AbortController()

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": this.#csrfToken() },
        body: JSON.stringify({ raw }),
        signal: this.abortController.signal
      })
      const result = await response.json()
      this.#applyResult(result)
    } catch (error) {
      if (isAbortError(error)) return
      this.#showFallback()
    }
  }

  countryChanged() {
    const key = this.countrySelectTarget.value
    this.#showTier(key)
    this.#setCountryCode(this.#countryCodeFor(key))
  }

  #applyResult(result) {
    if (result.confidence === "none" || !result.tier) {
      this.#showFallback()
      return
    }
    const key = this.#tierKeyFor(result.tier, result.parsed?.country_code)
    this.#showTier(key)
    this.#prefill(key, result.parsed)
    this.#setCountryCode(this.#countryCodeFor(key, result.parsed?.country_code))
  }

  // Maps the backend's FormatScheme tier symbol to this view's 9 partial
  // keys. Only :iban is ambiguous (EU vs GB share the same validation tier
  // but different entry fields) — everything else is a direct rename, not
  // duplicated validation logic.
  #tierKeyFor(tier, countryCode) {
    if (tier === "iban") return countryCode === "GB" ? "gb" : "eu"
    const map = { local: "cz", aba: "us", institution_transit: "ca", bsb: "au", ifsc: "in", zengin: "jp" }
    return map[tier] || "other"
  }

  // Reverse of #tierKeyFor: what country_code should be submitted for the
  // currently active tier. Direct 1:1 tiers always win; :eu/:other fall back
  // to whatever country the detector itself parsed out of the pasted IBAN
  // (undefined when the tier came from the manual picker instead), leaving
  // the field blank rather than guessing.
  #countryCodeFor(key, parsedCountryCode) {
    return TIER_COUNTRY_CODES[key] || parsedCountryCode || ""
  }

  #showTier(key) {
    this.tierSectionTargets.forEach((section) => {
      const isActive = section.dataset.tierKey === key
      section.style.display = isActive ? "block" : "none"
      section.disabled = !isActive
    })
    if (this.hasCountrySelectTarget) this.countrySelectTarget.value = key
  }

  #showFallback() {
    // Falls back to whatever tier is already shown — do NOT touch
    // country_code here, or a garbage paste after a successful detection
    // would silently wipe out an already-correct value.
    this.#showTier(this.hasCountrySelectTarget ? this.countrySelectTarget.value : "other")
  }

  #prefill(key, parsed) {
    if (!parsed) return
    const section = this.tierSectionTargets.find((s) => s.dataset.tierKey === key)
    if (!section) return
    Object.entries(parsed).forEach(([field, value]) => {
      const input = section.querySelector(`[name$="[${field}]"]`)
      if (input && !input.value) input.value = value
    })
  }

  #setCountryCode(value) {
    if (this.hasCountryCodeInputTarget) this.countryCodeInputTarget.value = value || ""
  }

  #csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }
}
